import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Crown, Video, Phone, MessageSquare, Plus, Edit, Trash2, Save, DollarSign, Settings, BarChart3
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import SubscriptionSettings from "../components/SubscriptionSettings";
import SubscriptionMonitoringDashboard from "../components/SubscriptionMonitoringDashboard";
import SubscriptionPlansManagement from "../components/SubscriptionPlansManagement";

const AdminPricingManagement = () => {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currencySettings, setCurrencySettings] = useState(null);
  const [primaryCurrency, setPrimaryCurrency] = useState("USD");
  const [currencySymbol, setCurrencySymbol] = useState("$");
  
  const [showRateModal, setShowRateModal] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  
  const [rateForm, setRateForm] = useState({
    consultation_type: "video",
    rate_per_minute: 1.67,
    description: "",
    is_active: true
  });

  useEffect(() => {
    if (user?.role !== "admin" && user?.role !== "school_admin") {
      toast.error("Access denied: Admin only");
      navigate("/");
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [ratesRes, currencyRes] = await Promise.all([
        axios.get(`${API}/admin/consultation-rates`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { rates: [] } })),
        axios.get(`${API}/admin/currency-settings`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null }))
      ]);
      
      // Handle different response formats
      const ratesData = ratesRes.data?.rates || ratesRes.data || [];
      setRates(Array.isArray(ratesData) ? ratesData : []);
      
      setCurrencySettings(currencyRes.data);
      if (currencyRes.data?.base_currency) {
        setPrimaryCurrency(currencyRes.data.base_currency);
        setCurrencySymbol(currencyRes.data.base_currency === 'USD' ? '$' : currencyRes.data.base_currency === 'EUR' ? '€' : '₹');
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setRates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRate = () => {
    setEditingRate(null);
    setRateForm({
      consultation_type: "video",
      rate: 0,
      duration_minutes: 45,
      description: "",
      is_active: true
    });
    setShowRateModal(true);
  };

  const handleEditRate = (rate) => {
    setEditingRate(rate);
    setRateForm({
      consultation_type: rate.consultation_type,
      rate_per_minute: rate.rate_per_minute || rate.rate || 1.67,
      description: rate.description || "",
      is_active: rate.is_active
    });
    setShowRateModal(true);
  };

  const handleSaveRate = async () => {
    const data = {
      ...rateForm,
      rate_per_minute: parseFloat(rateForm.rate_per_minute)
    };

    try {
      if (editingRate) {
        await axios.patch(`${API}/admin/consultation-rates/${editingRate.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Rate updated");
      } else {
        await axios.post(`${API}/admin/consultation-rates`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Rate created");
      }
      setShowRateModal(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to save rate");
    }
  };

  const handleDeleteRate = async (id) => {
    if (!confirm("Delete this rate?")) return;
    try {
      await axios.delete(`${API}/admin/consultation-rates/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
        });
      toast.success("Rate deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete rate");
    }
  };

  const getConsultationIcon = (type) => {
    switch(type) {
      case "video": return <Video className="w-6 h-6 text-blue-500" />;
      case "voice": return <Phone className="w-6 h-6 text-green-500" />;
      case "chat": return <MessageSquare className="w-6 h-6 text-purple-500" />;
      default: return <Video className="w-6 h-6" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">Pricing Management</h1>
          <p className="text-gray-600">Manage subscription plans and consultation rates</p>
        </div>

        <Tabs defaultValue="settings" className="space-y-6">
          <TabsList className="grid grid-cols-4 w-full max-w-2xl">
            <TabsTrigger value="settings" className="flex items-center gap-1">
              <Settings className="w-4 h-4" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="plans" className="flex items-center gap-1">
              <Crown className="w-4 h-4" />
              Plans
            </TabsTrigger>
            <TabsTrigger value="monitoring" className="flex items-center gap-1">
              <BarChart3 className="w-4 h-4" />
              Monitoring
            </TabsTrigger>
            <TabsTrigger value="rates" className="flex items-center gap-1">
              <DollarSign className="w-4 h-4" />
              Rates
            </TabsTrigger>
          </TabsList>

          {/* Subscription Settings */}
          <TabsContent value="settings">
            <SubscriptionSettings API={API} token={token} />
          </TabsContent>

          {/* Subscription Plans */}
          <TabsContent value="plans">
            <SubscriptionPlansManagement API={API} token={token} />
          </TabsContent>

          {/* Consultation Rates */}
          <TabsContent value="rates">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Consultation Rates</h2>
              <Button onClick={handleCreateRate}>
                <Plus className="w-4 h-4 mr-2" />
                Add Rate
              </Button>
            </div>

            {!rates || rates.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <DollarSign className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600 mb-4">No consultation rates defined</p>
                  <Button onClick={handleCreateRate}>Add Your First Rate</Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {rates.map((rate) => (
                  <Card key={rate.id} className={`${!rate.is_active ? 'opacity-60' : ''}`}>
                    <CardHeader>
                      <div className="flex items-center justify-between mb-2">
                        {getConsultationIcon(rate.consultation_type)}
                        <Badge variant={rate.is_active ? "default" : "secondary"}>
                          {rate.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                      <CardTitle className="text-lg capitalize">{rate.consultation_type} Call</CardTitle>
                      <div className="text-2xl font-bold text-green-600 mt-2">
                        {currencySymbol}{rate.rate_per_minute || rate.rate}
                        <span className="text-sm font-normal text-gray-600">/min</span>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="mb-4 text-sm space-y-1">
                        <p className="text-gray-600">Per minute rate - No duration limit</p>
                        {rate.description && <p className="text-gray-600">{rate.description}</p>}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => handleEditRate(rate)} className="flex-1">
                          <Edit className="w-3 h-3 mr-1" />
                          Edit
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDeleteRate(rate.id)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Subscription Monitoring */}
          <TabsContent value="monitoring">
            <SubscriptionMonitoringDashboard API={API} token={token} />
          </TabsContent>
        </Tabs>

        {/* Rate Modal */}
        <Dialog open={showRateModal} onOpenChange={setShowRateModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingRate ? 'Edit' : 'Add'} Consultation Rate</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Consultation Type *</Label>
                <Select 
                  value={rateForm.consultation_type}
                  onValueChange={(val) => setRateForm({...rateForm, consultation_type: val})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="video">Video Call</SelectItem>
                    <SelectItem value="voice">Voice Call</SelectItem>
                    <SelectItem value="chat">Chat Session</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Rate Per Minute ({currencySymbol || primaryCurrency}) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={rateForm.rate_per_minute}
                  onChange={(e) => setRateForm({...rateForm, rate_per_minute: parseFloat(e.target.value)})}
                  placeholder="1.67"
                />
                <p className="text-xs text-gray-500 mt-1">Amount charged per minute (e.g., ₹1.67/min)</p>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={rateForm.description}
                  onChange={(e) => setRateForm({...rateForm, description: e.target.value})}
                  placeholder="Additional details about this consultation type..."
                  rows={3}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="rate_active"
                  checked={rateForm.is_active}
                  onChange={(e) => setRateForm({...rateForm, is_active: e.target.checked})}
                />
                <Label htmlFor="rate_active">Active (available for booking)</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRateModal(false)}>Cancel</Button>
              <Button onClick={handleSaveRate}>
                <Save className="w-4 h-4 mr-2" />
                {editingRate ? 'Update' : 'Add'} Rate
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Footer />
    </div>
  );
};

export default AdminPricingManagement;
